import ko from "knockout";
import $ from "jquery";
import tinymce, { Editor, RawEditorOptions } from "@lifecare-welfare/tinymce-enterprise";
import baseUrl from "flow/wrappers/baseUrl";
import tinyPageUpDownScrollFix from "flow/fixes/tinyPageUpDownScrollFix";
import session from "flow/session/session";
import register from "flow/knockout/register";
import { TinyMCEProductionKey } from "3pp/tinyLicenseKeys";
import { getTinyCulture } from "flow/localization/tinyCulture";
import { ITinySelectionRange } from "flow/interfaces/ITinySelectionRange";
import { ITinyMCEKnockoutWrapper } from "flow/interfaces/ITinyMCEKnockoutWrapper";

function createWrapper(
    element: HTMLElement,
    configObs: KnockoutObservable<RawEditorOptions>,
    textObs: KnockoutObservable<string>,
    hasFocusObs: KnockoutObservable<boolean>,
    disableGreedyHeight: boolean
): ITinyMCEKnockoutWrapper {
    let myEditor: Editor;
    let originalOptions: RawEditorOptions;
    let currentConfig: RawEditorOptions;
    let originalText = "";
    const isDirty = ko.observable(false);
    const subs: KnockoutSubscription[] = [];
    const fullscreenIndicatorClass = "custom-tox-in-fullscreen-mode";

    subs.push(
        configObs.subscribe(function (newConfig) {
            if (newConfig !== undefined) {
                create(newConfig);
            }
        })
    );

    subs.push(
        textObs.subscribe(function (newText) {
            if (newText !== getText()) {
                setText(newText);
            }
        })
    );

    subs.push(
        hasFocusObs.subscribe(function (hasFocus: boolean) {
            if (hasFocus) {
                requestFocus();
            }
        })
    );

    function commit() {
        if (myEditor) {
            myEditor.isNotDirty = true; // Force not dirty state
        }
        isDirty(false);
        originalText = getText();
    }

    function isInternetExplorer() {
        const ua = window.navigator.userAgent;

        const msie = ua.indexOf("MSIE ");
        if (msie > 0) {
            return true;
        }

        const trident = ua.indexOf("Trident/");
        if (trident > 0) {
            return true;
        }

        const edge = ua.indexOf("Edge/");
        if (edge > 0) {
            return true;
        }

        // other browser
        return false;
    }

    function sync() {
        textObs(getText());
    }

    function onEditorInitialized(editor: Editor) {
        myEditor = editor;
        setText(textObs());
        if (hasFocusObs()) {
            myEditor.focus();
        }

        myEditor.on("change keyup nodechange", function () {
            isDirty(myEditor.isDirty());
        });

        myEditor.on("blur", function () {
            sync();
            hasFocusObs(false);
        });

        myEditor.on("FullscreenStateChanged", event => {
            const htmlRoot = myEditor.getDoc().documentElement;
            if (event.state) {
                htmlRoot.classList.add(fullscreenIndicatorClass);
            } else {
                htmlRoot.classList.remove(fullscreenIndicatorClass);
            }
        });

        myEditor.on("click", session.registerActivity);
        myEditor.on("wheel", session.registerActivity);
        myEditor.on("keyup", session.registerActivity);
        myEditor.on("touchend", session.registerActivity);

        myEditor.formatter.register("myselectionformat", {
            inline: "span",
            classes: "lc-editor-selected-text"
        });

        if (originalOptions.init_instance_callback !== undefined) {
            originalOptions.init_instance_callback(editor);
        }

        if (isInternetExplorer()) {
            window.addEventListener("hashchange", handleFullscreenMode, false);
        } else {
            window.addEventListener("popstate", handleFullscreenMode);
        }

        myEditor.on("Remove", function () {
            if (isInternetExplorer()) {
                window.removeEventListener("hashchange", handleFullscreenMode, false);
            } else {
                window.removeEventListener("popstate", handleFullscreenMode);
            }
        });

        if (currentConfig.fixPageUpDown) {
            tinyPageUpDownScrollFix(myEditor, tinymce);
        }

        window.setTimeout(function () {
            recalculateSize();

            if ((window as any).chrome) {
                window.setTimeout(function () {
                    if (myEditor) {
                        const containerElem = myEditor.getContainer();
                        const $container = $(containerElem);
                        $container.hide().show(0);
                    }
                }, 500);
            }
        }, 1);
    }

    function handleFullscreenMode() {
        if (inFullscreenMode()) {
            myEditor.editorCommands.execCommand("mceFullscreen");
        }
    }

    function recalculateSize() {
        if (disableGreedyHeight) {
            return;
        }

        if (!myEditor) {
            return;
        }

        if (currentConfig.height) {
            return;
        }

        if (inFullscreenMode()) {
            return;
        }

        const rootElement = $(element).closest(".lc-editor-size-root");

        const containerElem = myEditor.getContainer();
        const container = $(containerElem);

        const body = container.find("iframe").first();

        const uiHeight = container.outerHeight() - body.height();

        const contenders = rootElement.find(".lc-editor-size-contender:visible");

        let usedHeight = 0;
        contenders.each(function (_index, contender) {
            usedHeight += $(contender).outerHeight(true);
        });

        let remainingHeight: number | string = rootElement.height() - uiHeight - usedHeight - 1;
        remainingHeight = remainingHeight > 0 ? remainingHeight : 0;

        if (currentConfig.minHeight && typeof currentConfig.minHeight === "number") {
            remainingHeight = Math.max(remainingHeight, currentConfig.minHeight);
        } else if (currentConfig.minHeight && typeof currentConfig.minHeight === "string") {
            remainingHeight = currentConfig.minHeight;
        } else {
            remainingHeight = Math.max(remainingHeight, 200);
        }

        body.height(remainingHeight);
    }

    function onEditorSetup(editor: Editor) {
        if (originalOptions.setup != undefined) {
            originalOptions.setup(editor);
        }
    }

    function editorHasBeenInitialized() {
        return myEditor !== undefined;
    }

    function checkEditorInitialized() {
        if (!editorHasBeenInitialized()) {
            throw new Error("Editor not initialized yet!");
        }
    }

    function create(options: RawEditorOptions) {
        if (options === undefined || options === null) {
            return;
        }

        originalText = textObs();

        currentConfig = options;

        originalOptions = {
            init_instance_callback: options.init_instance_callback,
            setup: options.setup
        };

        const overrides = {
            init_instance_callback: onEditorInitialized,
            setup: onEditorSetup
        };

        const newOptions = Object.assign(
            {
                language: getTinyCulture(),
                license_key: TinyMCEProductionKey,
                target: element,
                base_url: baseUrl + "dist"
            },
            options,
            overrides
        );

        // Support multiple create on the same element
        destroyEditorIfAny();

        tinymce.init(newOptions);
    }

    // To prevent a memory leak, ensure that the underlying element's disposal destroys it's associated editor.
    ko.utils["domNodeDisposal"].addDisposeCallback(element, function () {
        destroyEditorIfAny();
        subs.forEach(function (sub) {
            sub.dispose();
        });
    });

    function destroyEditorIfAny() {
        if (myEditor) {
            myEditor.off("click", session.registerActivity);
            myEditor.off("wheel", session.registerActivity);
            myEditor.off("keyup", session.registerActivity);
            myEditor.off("touchend", session.registerActivity);

            myEditor.remove();
            myEditor = undefined;
        }
    }

    function setText(text: string) {
        if (text === undefined || text === null) {
            text = "";
        }

        if (editorHasBeenInitialized()) {
            originalText = text;

            myEditor.setContent(text);
            myEditor.undoManager.clear();
            myEditor.undoManager.add();
            myEditor.isNotDirty = true; // Force not dirty state
            isDirty(false);
        }

        recalculateSize();
    }

    function getText() {
        if (editorHasBeenInitialized()) {
            const text = myEditor.getContent();

            return text;
        }
        return "";
    }

    function revert() {
        if (editorHasBeenInitialized()) {
            textObs(originalText);
            myEditor.isNotDirty = true; // Force not dirty state
            isDirty(false);
        }
    }

    function getSelectionRange(): ITinySelectionRange {
        checkEditorInitialized();

        const selection = myEditor.selection;
        const range = selection.getRng();

        const selectionRange: ITinySelectionRange = {
            xpathBegin: calcXpath(range.startContainer),
            startOffset: range.startOffset,
            xpathEnd: calcXpath(range.endContainer),
            endOffset: range.endOffset
        };

        return selectionRange;
    }

    function getSelectionText(): string {
        checkEditorInitialized();

        const selection = myEditor.selection;

        return selection.getContent();
    }

    function highlightAndScrollToRange(range: ITinySelectionRange) {
        selectRangeByXpath(range);

        myEditor.formatter.apply("myselectionformat");

        $(myEditor.getBody()).find(".lc-editor-selected-text").get(0).scrollIntoView();
    }

    function scrollToHardSelected() {
        if (isInternetExplorer()) {
            const target = $(myEditor.getBody()).find(".lc-editor-selected-text").get(0);
            $(myEditor.getBody()).parent().scrollTop(target.offsetTop);
        } else {
            $(myEditor.getBody()).find(".lc-editor-selected-text").get(0).scrollIntoView();
        }
    }

    function selectRangeByXpath(range: ITinySelectionRange) {
        checkEditorInitialized();

        const startNode = getElementByXpath(range.xpathBegin);
        const endNode = getElementByXpath(range.xpathEnd);

        const domRange = myEditor.getDoc().createRange();
        domRange.setStart(startNode, range.startOffset);
        domRange.setEnd(endNode, range.endOffset);
        //domRange.startContainer = startNode;
        //domRange.startOffset = range.startOffset;
        //domRange.endContainer = endNode;
        //domRange.endOffset = range.endOffset;

        myEditor.selection.setRng(domRange);
    }

    function calcXpath(elem: Node) {
        const paths: string[] = [];

        // Use nodeName (instead of localName) so namespace prefix is included (if any).
        //    for (; elem && !($(elem).get(0).nodeType === Node.DOCUMENT_NODE) ; elem = elem.parentNode) {
        for (; elem && !$(elem).hasClass("mce-content-body"); elem = elem.parentNode) {
            let index = 0;
            for (let sibling = elem.previousSibling; sibling; sibling = sibling.previousSibling) {
                // Ignore document type declaration.
                if (sibling.nodeType == Node.DOCUMENT_TYPE_NODE) continue;

                if (sibling.nodeName == elem.nodeName) ++index;
            }

            const tagName = elem.nodeName.toLowerCase();
            const pathIndex = index ? "[" + (index + 1) + "]" : "[1]";
            paths.splice(0, 0, tagName + pathIndex);
        }

        return "/" + paths.join("/");
    }

    function getElementByXpath(path: string) {
        const iframeDocument = myEditor.getDoc();
        const root = myEditor.getBody();
        const xpath = path.replace("#text", "text()");

        const resultType = 9; // XPathResult.FIRST_ORDERED_NODE_TYPE, IE will not even let us check if XPathResult exists!
        const result = iframeDocument.evaluate(xpath, root, null, resultType, null);
        const node = result.singleNodeValue;
        return node;
    }

    function forceIsDirty() {
        myEditor.isNotDirty = false;
        isDirty(true);
    }

    function scrollToBottom() {
        myEditor.getWin().scrollTo(0, myEditor.getBody().scrollHeight);
    }

    function inFullscreenMode() {
        if (myEditor && myEditor.plugins.fullscreen && myEditor.plugins.fullscreen.isFullscreen()) {
            return true;
        }
        return false;
    }

    function toggleFullscreen() {
        if (myEditor) {
            const isDirtyBeforeToggle = isDirty();
            myEditor.editorCommands.execCommand("mceFullscreen");
            if (isDirtyBeforeToggle === false) {
                myEditor.isNotDirty = true; // Force not dirty state
                isDirty(false);
            }
        }
    }

    function requestFocus() {
        if (myEditor) {
            myEditor.focus();
        }
    }

    create(configObs());

    return {
        isDirty,
        revert,
        getSelectionRange,
        getSelectionText,
        commit,
        forceIsDirty,
        recalculateSize,
        selectRangeByXpath,
        highlightAndScrollToRange,
        scrollToHardSelected,
        scrollToBottom,
        sync,
        toggleFullscreen,
        requestFocus
    };
}

register.registerKnockoutBinding("editor", {
    init: function (element, valueAccessor) {
        const configuration = valueAccessor();

        const handle = configuration.handle;
        const configObs = configuration.config;
        const textObs = configuration.text ? configuration.text : ko.observable();
        const hasFocusObs = configuration.hasFocus ? configuration.hasFocus : ko.observable(false);
        const disableGreedyHeight = configuration.disableGreedyHeight ? true : false;

        const wrapper = createWrapper(element, configObs, textObs, hasFocusObs, disableGreedyHeight);
        handle(wrapper);
    }
});
